// =============================
// FiveCardDraw.jsx
// =============================
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gameService from '../../../services/gameService';
import { AuthContext } from '../../../context/AuthContext';
import { Button, Card, Spinner, Row, Col, Alert } from 'react-bootstrap';
import {
  FaCoins,
  FaHandPaper,
  FaGamepad,
  FaCrown,
  FaRegGem,
  FaPlayCircle,
  FaHistory,
  FaInfoCircle,
  FaArrowLeft,
} from 'react-icons/fa';
import './FiveCardDraw.css';

const FiveCardDraw = () => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const [betAmount, setBetAmount] = useState(1);
  const [playerHand, setPlayerHand] = useState([]);
  const [initialHand, setInitialHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [playerResult, setPlayerResult] = useState('');
  const [dealerResult, setDealerResult] = useState('');
  const [gameResult, setGameResult] = useState('');
  const [newBalance, setNewBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [history, setHistory] = useState([]);
  const [cardsToExchange, setCardsToExchange] = useState([]);
  const navigate = useNavigate();

  const getCardImage = (card) => `${process.env.PUBLIC_URL}/images/games/cards/${card}.png`;

  const handleStartGame = async () => {
    setLoading(true);
    setError(null);
    setCardsToExchange([]);
    setPlayerHand([]);
    setInitialHand([]);
    setDealerHand([]);
    setPlayerResult('');
    setDealerResult('');
    setGameResult('');
    try {
      const response = await gameService.startFiveCardDraw(betAmount);
      setPlayerHand(response.playerHand);
      setInitialHand(response.playerHand);
      setNewBalance(response.newBalance);
    } catch (error) {
      setError(error.response?.data?.error || t('fivecard.error_start'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gameService.playFiveCardDraw(betAmount);
      setPlayerHand(response.playerHand);
      setDealerHand(response.dealerHand);
      setPlayerResult(response.playerResult);
      setDealerResult(response.dealerResult);
      setGameResult(response.result);
      setNewBalance(response.newBalance);
    } catch (error) {
      setError(error.response?.data?.error || t('fivecard.error_play'));
    } finally {
      setLoading(false);
    }
  };

  const handleExchange = async () => {
    setLoading(true);
    setError(null);
    const indices = cardsToExchange.map((i) => parseInt(i, 10));
    try {
      const response = await gameService.exchangeFiveCardDraw(indices);
      setPlayerHand(response.playerHand);
      setDealerHand(response.dealerHand);
      setPlayerResult(response.playerResult);
      setDealerResult(response.dealerResult);
      setGameResult(response.result);
      setNewBalance(response.newBalance);
    } catch (error) {
      setError(error.response?.data?.error || t('fivecard.error_exchange'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const fetchedHistory = await gameService.getFiveCardDrawHistory();
      setHistory(fetchedHistory);
      setShowHistory(true);
    } catch (error) {
      setError(t('fivecard.error_history'));
    } finally {
      setLoading(false);
    }
  };

  const toggleCardSelection = (index) => {
    setCardsToExchange((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <div className="casino-container d-flex justify-content-center align-items-center">
      <Card className="text-center casino-card shadow-lg">
        <Card.Body>
          <div className="position-relative mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              className="casino-back-button"
              aria-label={t('common.back') || 'Volver'}
            >
              <FaArrowLeft className="me-2" />
            </Button>
            <h1 className="casino-title text-center">
              <FaCrown className="text-warning me-2" /> {t('fivecard.title')}{' '}
              <FaCrown className="text-warning ms-2" />
            </h1>
          </div>

          {!playerHand.length && (
            <div className="mb-3">
              <div className="input-group casino-bet-input-group">
                <span className="input-group-text casino-coin-icon">
                  <FaCoins />
                </span>
                <input
                  type="number"
                  className="form-control casino-input"
                  value={betAmount}
                  onChange={(e) => setBetAmount(Math.max(1, Number(e.target.value)))}
                  min="1"
                />
                <span className="input-group-text casino-coin-text">MENEL</span>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-center flex-wrap gap-2 mt-3">
            {!playerHand.length ? (
              <Button
                className="casino-button small-button casino-play-button"
                onClick={handleStartGame}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaPlayCircle className="me-2" /> {t('fivecard.start')}
                  </>
                )}
              </Button>
            ) : !gameResult ? (
              <>
                <Button
                  className="casino-button small-button casino-play-button"
                  onClick={handlePlay}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FaPlayCircle className="me-2" /> {t('fivecard.play')}
                    </>
                  )}
                </Button>
                <Button
                  className="casino-button small-button casino-exchange-button"
                  onClick={handleExchange}
                  disabled={loading || cardsToExchange.length === 0}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FaHandPaper className="me-2" /> {t('fivecard.exchange')}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                className="casino-button small-button casino-play-button"
                onClick={handleStartGame}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaPlayCircle className="me-2" /> {t('fivecard.play_again')}
                  </>
                )}
              </Button>
            )}
            <Button
              className="casino-button small-button casino-history-button"
              onClick={fetchHistory}
            >
              <FaHistory className="me-2" /> {t('fivecard.history')}
            </Button>
            <Button
              className="casino-button small-button casino-rules-button"
              onClick={() => setShowRules(true)}
            >
              <FaInfoCircle className="me-2" /> {t('fivecard.rules')}
            </Button>
          </div>

          {error && (
            <Alert variant="danger" className="mt-3" aria-live="polite">
              {error}
            </Alert>
          )}

          {initialHand.length > 0 && !gameResult && (
            <div className="mt-4">
              <h4 className="casino-hand-title">{t('fivecard.initial_hand')}</h4>
              <div className="d-flex justify-content-center casino-cards-container">
                {initialHand.map((card, index) => {
                  const selected = cardsToExchange.includes(index);
                  return (
                    <div
                      key={index}
                      className={`casino-playing-card ${selected ? 'selected' : ''}`}
                      onClick={() => toggleCardSelection(index)}
                      data-selected={selected}
                      title={t('fivecard.click_to_exchange')}
                    >
                      <img
                        src={getCardImage(card.card)}
                        alt={card.card}
                        className="casino-playing-card-image"
                      />
                    </div>
                  );
                })}
              </div>
              <p className="casino-hint">{t('fivecard.click_to_exchange')}</p>
            </div>
          )}

          {gameResult && (
            <div className="mt-4" aria-live="polite">
              <h3 className={`casino-result ${gameResult ? `result-${gameResult}` : ''}`}>
                {gameResult === 'win' ? (
                  <FaCrown className="text-success" />
                ) : gameResult === 'lose' ? (
                  <FaRegGem className="text-danger" />
                ) : (
                  <FaRegGem className="text-warning" />
                )}
                {gameResult === 'win'
                  ? ` ${t('fivecard.result.win')}`
                  : gameResult === 'lose'
                  ? ` ${t('fivecard.result.lose')}`
                  : ` ${t('fivecard.result.tie')}`}
              </h3>
              <p className="casino-balance">
                {t('fivecard.balance')}: {newBalance} MENEL
              </p>
              <Row>
                <Col>
                  <h4 className="casino-hand-title">
                    {t('fivecard.final_hand')} ({playerResult})
                  </h4>
                  <div className="d-flex justify-content-center casino-cards-container">
                    {playerHand.map((card, index) => (
                      <div key={index} className="casino-playing-card">
                        <img
                          src={getCardImage(card.card)}
                          alt={card.card}
                          className="casino-playing-card-image"
                        />
                      </div>
                    ))}
                  </div>
                </Col>
                <Col>
                  <h4 className="casino-hand-title">
                    {t('fivecard.dealer_hand')} ({dealerResult})
                  </h4>
                  <div className="d-flex justify-content-center casino-cards-container">
                    {dealerHand.map((card, index) => (
                      <div key={index} className="casino-playing-card">
                        <img
                          src={getCardImage(card.card)}
                          alt={card.card}
                          className="casino-playing-card-image"
                        />
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {showHistory && (
        <div className="casino-custom-modal">
          <div className="casino-modal-content">
            <div className="casino-modal-header">
              <h2>
                <FaHistory className="me-2" /> {t('fivecard.history')}
              </h2>
              <button
                className="casino-modal-close"
                onClick={() => setShowHistory(false)}
                aria-label={t('common.close') || 'Cerrar'}
              >
                &times;
              </button>
            </div>
            <div
              className="casino-modal-body"
              style={{ maxHeight: '400px', overflowY: 'auto' }}
            >
              {loading ? (
                <Spinner animation="border" />
              ) : (
                <ul className="list-group">
                  {history.length > 0 ? (
                    history.map((entry, index) => (
                      <li
                        key={index}
                        className={`list-group-item history-${entry.result}`}
                      >
                        <strong>{t('fivecard.result')}:</strong> {entry.result}
                        <br />
                        <strong>{t('fivecard.score')}:</strong> {entry.score}
                        <br />
                        <strong>{t('fivecard.details')}:</strong> {entry.details}
                        <br />
                        <strong>{t('fivecard.date')}:</strong>{' '}
                        {new Date(entry.created_at).toLocaleString()}
                      </li>
                    ))
                  ) : (
                    <p>{t('fivecard.no_history')}</p>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showRules && (
        <div className="casino-custom-modal">
          <div className="casino-modal-content">
            <div className="casino-modal-header">
              <h2>
                <FaGamepad className="me-2" /> {t('fivecard.rules')}
              </h2>
              <button
                className="casino-modal-close"
                onClick={() => setShowRules(false)}
                aria-label={t('common.close') || 'Cerrar'}
              >
                &times;
              </button>
            </div>
            <div
              className="casino-modal-body"
              style={{ maxHeight: '400px', overflowY: 'auto' }}
            >
              <h5>{t('fivecard.rule_title')}</h5>
              <p>{t('fivecard.rule_intro')}</p>
              <ul>
                <li>{t('fivecard.rule.highcard')}</li>
                <li>{t('fivecard.rule.pair')}</li>
                <li>{t('fivecard.rule.twopair')}</li>
                <li>{t('fivecard.rule.three')}</li>
                <li>{t('fivecard.rule.straight')}</li>
                <li>{t('fivecard.rule.flush')}</li>
                <li>{t('fivecard.rule.fullhouse')}</li>
                <li>{t('fivecard.rule.four')}</li>
                <li>{t('fivecard.rule.straightflush')}</li>
                <li>{t('fivecard.rule.royalflush')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiveCardDraw;

